import {
	DefaultButtonCompat as DefaultButton,
	DialogFooterCompat as DialogFooter,
	PrimaryButtonCompat as PrimaryButton,
	TooltipHostCompat as TooltipHost,
} from 'components/FluentUICompat';
import {useTranslation} from 'react-i18next';
import React from 'react';
import {Requirement, RequirementChangeType} from 'types';
import {EnumDropdown} from 'components/hookForms';
import {useForm} from 'react-hook-form';
import {ReasonField} from 'features/RegulatoryDocuments/components/DocumentDetails/WorkflowDialog/ReasonField';
import {Icon} from '@fluentui/react';

export enum SendApprovalDialogChangeTypeMode {
	Show,
	Hide,
	Disable,
}

export const WORKFLOW_DIALOG_MODAL_PROPS = {
	isBlocking: true,
	styles: {main: {maxWidth: 450}},
} as const;

type WorkflowChangeTypeDropdownProps = {
	changeType: RequirementChangeType;
	changeTypeMode: SendApprovalDialogChangeTypeMode;
	setChangeType: (changeType: RequirementChangeType) => void;
};

export const WorkflowChangeTypeDropdown: React.FC<
	WorkflowChangeTypeDropdownProps
> = ({changeType, changeTypeMode, setChangeType}) => {
	const {t} = useTranslation('features/requirements', {
		keyPrefix: 'WorkflowDialog',
	});

	type FormFields = Pick<Requirement, 'changeType'>;
	const {control, getValues} = useForm<FormFields>({
		reValidateMode: 'onSubmit',
		mode: 'all',
	});

	const onChangeTypeChange = () => {
		setChangeType(getValues('changeType'));
	};

	const onRenderLabel = (el: any) => (
		<div style={{marginBottom: '4px'}}>
			<span style={{fontWeight: '600', fontSize: '14px'}}>{el?.label} </span>
			<TooltipHost content={t('ApprovalChangeTypeTooltip')}>
				<Icon
					iconName='Info'
					className='hoverable'
					aria-label='Info'
					style={{color: '#B12525'}}
				/>
			</TooltipHost>
		</div>
	);

	if (changeTypeMode === SendApprovalDialogChangeTypeMode.Hide) return null;

	return (
		<EnumDropdown
			required={true}
			control={control}
			name={'changeType'}
			label={t('ApprovalChangeTypeLabel')}
			onRenderLabel={onRenderLabel}
			placeholder={t('ApprovalChangeTypePlaceholder')}
			defaultValue={changeType}
			enumName={'RequirementChangeType'}
			enumType={RequirementChangeType}
			exclude={[RequirementChangeType.None]}
			onChange={onChangeTypeChange}
			disabled={changeTypeMode === SendApprovalDialogChangeTypeMode.Disable}
		/>
	);
};

type WorkflowReasonFieldProps = {
	setReason: (res: string) => void;
};

export const WorkflowReasonField: React.FC<WorkflowReasonFieldProps> = ({
	setReason,
}) => {
	const {t} = useTranslation('features/requirements', {
		keyPrefix: 'WorkflowDialog',
	});

	const onChange = React.useCallback(
		(_: any, val?: string) => setReason(val ?? ''),
		[setReason],
	);

	return (
		<ReasonField
			onChange={onChange}
			multiline
			autoAdjustHeight
			maxLength={488}
			label={t('CommentField')}
		/>
	);
};

type WorkflowDialogFooterProps = {
	onConfirm?: () => void;
	onDismiss?: () => void;
	submitDisabled?: boolean;
};

export const WorkflowDialogFooter: React.FC<WorkflowDialogFooterProps> = ({
	onConfirm,
	onDismiss,
	submitDisabled,
}) => {
	const {t} = useTranslation('features/requirements', {
		keyPrefix: 'WorkflowDialog',
	});

	return (
		<DialogFooter>
			<PrimaryButton onClick={onConfirm} disabled={submitDisabled}>
				{t('ApprovalConfirmButton')}
			</PrimaryButton>
			<DefaultButton onClick={onDismiss} text={t('CancelButton')} />
		</DialogFooter>
	);
};
